import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useAuth } from '../app/Auth';
import { Chip, ErrorNote, Icon, PageHeader } from '../app/ui';
import { callFn } from '../lib/api';
import { ageLabel } from '../lib/format';
import { getLastSection } from '../lib/section';
import { useApi } from '../lib/useApi';
import type { ChatMessage, ConversationRow, SendResponse } from '../lib/types';
import { FilesPanel } from './AssistantFiles';
import { RichMessage } from './Rich';

function Bubble({ m }: { m: ChatMessage }) {
  const user = m.role === 'user';
  return (
    <div className={`bubble-row ${user ? 'me' : 'them'}`}>
      <div className={`bubble ${user ? 'user' : 'assistant'}`}>
        {user ? <p className="pre">{m.content}</p> : <RichMessage content={m.content} />}
        {m.attachments?.length ? <div className="row wrap gap">{m.attachments.map((a) => <Chip key={a.id}>{a.name}</Chip>)}</div> : null}
        {m.actions?.length ? (
          <div className="row wrap gap actions-row">
            {m.actions.map((a, n) => (
              <Chip key={n} tone={a.ok ? 'green' : 'red'} title={a.error ?? a.summary ?? ''}>{a.type}{a.ref ? ` ${a.ref}` : ''}{a.ok ? '' : ' failed'}{a.summary ? ` — ${a.summary}` : a.error ? ` — ${a.error}` : ''}</Chip>
            ))}
          </div>
        ) : null}
        {!user && (m.model || m.ms) ? <div className="muted small meta-line">{[m.provider, m.model, m.ms ? `${(m.ms / 1000).toFixed(1)} s` : ''].filter(Boolean).join(' · ')}</div> : null}
      </div>
    </div>
  );
}

export function AssistantPage() {
  const { me, refreshMe, toast } = useAuth();
  const convs = useApi<{ conversations: ConversationRow[] }>('assistant_conversations');
  const [cid, setCid] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [filesKey, setFilesKey] = useState(0);
  const end = useRef<HTMLDivElement>(null);
  const name = me?.company.assistant?.name || 'the Assistant';

  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, sending]);

  const open = useCallback(async (id: string | null) => {
    setError(null); setCid(id);
    if (!id) { setMessages([]); return; }
    setLoadingConv(true);
    try { const r = await callFn<{ messages: ChatMessage[] }>('assistant_messages', { conversationId: id }); setMessages(r.messages); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoadingConv(false); }
  }, []);

  async function send() {
    const t = text.trim();
    if (!t || sending) return;
    setText(''); setError(null); setSending(true);
    const temp: ChatMessage = { id: `tmp-${Date.now()}`, role: 'user', content: t };
    setMessages((m) => [...m, temp]);
    try {
      // One turn can take 20 s+ through Agent-Control; assistant_send is request/response (no streaming action yet).
      const r = await callFn<SendResponse>('assistant_send', { text: t, ...(cid ? { conversationId: cid } : {}), ui: { section: getLastSection() } });
      setCid(r.conversationId);
      setMessages((m) => [...m, r.message]);
      if (r.message.actions?.some((a) => a.ok)) void refreshMe();
      if (r.message.actions?.some((a) => a.ok && a.type === 'write_file')) setFilesKey((k) => k + 1);
      void convs.reload(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      setText(t);
      toast('The Assistant could not answer', 'err');
    } finally { setSending(false); }
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); }
  };

  const list = convs.data?.conversations ?? [];
  return (
    <section className="page assistant">
      <PageHeader title="Assistant">
        <select aria-label="Conversation" value={cid ?? ''} onChange={(e) => void open(e.target.value || null)} className="conv-select">
          <option value="">New conversation</option>
          {list.map((c) => <option key={c.id} value={c.id}>{c.title || 'Untitled'} · {ageLabel(c.updatedAt)}</option>)}
        </select>
        <button className={`btn small${showFiles ? ' on' : ''}`} onClick={() => setShowFiles((v) => !v)} aria-pressed={showFiles}>Files</button>
        <button className="icon-btn" onClick={() => void open(null)} aria-label="New conversation"><Icon name="plus" /></button>
      </PageHeader>
      {showFiles ? <FilesPanel reloadKey={filesKey} /> : null}
      <div className="chat" aria-live="polite">
        {!messages.length && !loadingConv ? <div className="empty">Ask {name} where things stand — “what's waiting on me?”, “where are we on Rex?”, “file this as an initiative…”.</div> : null}
        {loadingConv ? <div className="muted">Loading…</div> : null}
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        {sending ? <div className="bubble-row them"><div className="bubble assistant typing"><span className="spinner" /> {name} is thinking…</div></div> : null}
        <div ref={end} />
      </div>
      <ErrorNote text={error} />
      <div className="composer">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} placeholder={`Message ${name}`} aria-label="Message" />
        <button className="btn primary" disabled={sending || !text.trim()} onClick={() => void send()}>Send</button>
      </div>
    </section>
  );
}
